import { useState, useCallback } from 'react';

interface UseCopyLinkReturn {
  isCopied: boolean;
  copyToClipboard: (text: string) => Promise<boolean>;
  resetCopyStatus: () => void;
}

const useCopyLink = (resetDelay: number = 2000): UseCopyLinkReturn => {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = useCallback(
    async (text: string): Promise<boolean> => {
      if (!text) return false;

      try {
        await navigator.clipboard.writeText(text);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), resetDelay);

        return true;
      } catch (err) {
        console.error(err);
        try {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);

          setIsCopied(true);
          setTimeout(() => setIsCopied(false), resetDelay);

          return true;
        } catch (fallbackErr) {
          console.error(fallbackErr);
          return false;
        }
      }
    },
    [resetDelay]
  );

  const resetCopyStatus = useCallback(() => {
    setIsCopied(false);
  }, []);

  return {
    isCopied,
    copyToClipboard,
    resetCopyStatus,
  };
};

export default useCopyLink;
